/**
 * Times of day as minutes after midnight (0–1439), plus the forgiving parser
 * behind the typed time fields.
 */

export const START_OF_DAY = 0;
export const NOON = 720;
/** 11:59 PM — the last minute of a day, which is how a range "ends on" a date. */
export const END_OF_DAY = 1439;

const STEP_MINUTES = 15;
const SLOT_MINUTES = 30;

/** Every half hour, plus 11:59 PM so a day can be picked to its end. */
export const TIME_SLOTS: readonly number[] = [
  ...Array.from({ length: 1440 / SLOT_MINUTES }, (_, i) => i * SLOT_MINUTES),
  END_OF_DAY,
];

const pad = (n: number) => String(n).padStart(2, '0');

/** "9:30 PM". */
export function formatTimeOfDay(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${pad(minutes % 60)} ${hour < 12 ? 'AM' : 'PM'}`;
}

/** "HH:mm", 24-hour. */
export function timeOfDayToClock(minutes: number): string {
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

const TIME_RE = /^(\d{1,2})(?::?(\d{2}))?(a|am|p|pm)?$/;
/** A bare hour from 1 to 6 means the afternoon: nothing here starts at 3 AM. */
const AFTERNOON_GUESS_MAX_HOUR = 6;

function to24Hour(hour: number, meridiem: string): number | null {
  if (hour < 1 || hour > 12) return null;
  if (meridiem.startsWith('a')) return hour === 12 ? 0 : hour;
  return hour === 12 ? 12 : hour + 12;
}

function guessHour(hourText: string, hasMinutes: boolean): number | null {
  const hour = Number(hourText);
  if (hour > 23) return null;
  const bareSmallHour = !hasMinutes && !hourText.startsWith('0');
  return bareSmallHour && hour >= 1 && hour <= AFTERNOON_GUESS_MAX_HOUR ? hour + 12 : hour;
}

/**
 * Reads what someone types into a time field: 9, 9a, 9:30pm, 930p, 21:30,
 * noon, midnight. Null when it is not a time.
 */
export function parseTimeOfDay(raw: string): number | null {
  const text = raw.trim().toLowerCase().replace(/[\s.]/g, '');
  if (text === 'noon') return NOON;
  if (text === 'midnight') return START_OF_DAY;
  const match = TIME_RE.exec(text);
  if (!match) return null;
  const [, hourText, minuteText, meridiem] = match;
  const minute = minuteText ? Number(minuteText) : 0;
  if (minute > 59) return null;
  const hour = meridiem
    ? to24Hour(Number(hourText), meridiem)
    : guessHour(hourText, Boolean(minuteText));
  return hour === null ? null : hour * 60 + minute;
}

/** Moves a time 15 minutes up or down, held inside the day. */
export function stepTimeOfDay(minutes: number, direction: 1 | -1): number {
  return Math.min(END_OF_DAY, Math.max(START_OF_DAY, minutes + direction * STEP_MINUTES));
}

/** The words beside the landmark slots in the time list. */
export function timeSlotNote(minutes: number): string | null {
  if (minutes === START_OF_DAY) return 'start of day';
  if (minutes === NOON) return 'noon';
  if (minutes === END_OF_DAY) return 'end of day';
  return null;
}
